import {
  Flex,
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  Image,
  useColorModeValue,
  Divider,
} from "@chakra-ui/react";
import {
  Display03,
  Label01,
  Paragraph02,
  Paragraph03,
} from "~/components/Texts";
import OnboardingNavigation from "~/components/onboarding/OnboardingNavigation";
import { useTheme } from "@chakra-ui/react";
import { useState } from "react";
import {
  currencyIsoToCurrencyName,
  countryIsoToCurrencyIso,
} from "~/utils/Formatters";
import { FiChevronRight } from "react-icons/fi";

const i18nCountries = require("i18n-iso-countries");
i18nCountries.registerLocale(require("i18n-iso-countries/langs/es.json"));

const SUPPORTED_COUNTRIES = ["ARG", "CHL", "COL", "MEX", "URY"];

export default function OnboardingShopRegion({
  onboardingData,
  onBack,
  onComplete,
  showBack,
}) {
  const [countryIso, setCountryIso] = useState(
    onboardingData.countryIso ? onboardingData.countryIso : "ARG"
  );

  const handleComplete = () => {
    onComplete({ countryIso: countryIso }, null);
  };

  const isStepReady = () => {
    return countryIso;
  };

  return (
    <Flex flex="1" direction="column">
      <Box flex="1">
        <VStack height="100%" width="100%" spacing="4" align="flex-start">
          <Display03>País de tu tienda</Display03>
          <Paragraph02>
            El país de tu tienda determina la moneda de tu tienda, como así
            también te permite aceptar medios de pago y métodos de entrega que
            estén disponibles en tu región.
          </Paragraph02>
          <Paragraph02>
            <b>Seleccioná el país en donde vas a vender tus productos</b>
          </Paragraph02>
          <HStack w="100%" spacing="8" pt="4" align="flex-start">
            <SimpleGrid
              flex={{ base: "1", md: "2" }}
              spacing="2"
              columns={{ base: "1", md: "2", lg: "3" }}
            >
              {SUPPORTED_COUNTRIES.map((supportedCountryIso) => (
                <CountryButton
                  key={`country_flag_${supportedCountryIso}`}
                  countryIso={supportedCountryIso}
                  onCountrySelected={(newCountryIso) =>
                    setCountryIso(newCountryIso)
                  }
                  isSelected={supportedCountryIso === countryIso}
                />
              ))}
            </SimpleGrid>
            <VStack flex="1" height="100%" align="flex-start">
              <Label01>
                {i18nCountries.getName(countryIso, "es", {
                  select: "official",
                })}
              </Label01>
              <Paragraph02>
                {currencyIsoToCurrencyName(countryIsoToCurrencyIso(countryIso))}{" "}
                ({countryIsoToCurrencyIso(countryIso)})
              </Paragraph02>
              <Paragraph02 pt="4">
                <b>Medios de pago disponibles</b>
              </Paragraph02>
              <VStack w="100%" align="flex-start">
                {availablePaymentMethodsForCountryIso(countryIso).map(
                  (method) => (
                    <HStack w="100%">
                      <Paragraph03>
                        <FiChevronRight />
                      </Paragraph03>
                      <Paragraph03>
                        {paymentMethodToPrettyName(method)}
                      </Paragraph03>
                    </HStack>
                  )
                )}
              </VStack>
              <Paragraph02 pt="4">
                <b>Métodos de entrega disponibles</b>
              </Paragraph02>
              <VStack w="100%" align="flex-start">
                {availableShippingMethodsForCountryIso(countryIso).map(
                  (method) => (
                    <HStack w="100%">
                      <Paragraph03>
                        <FiChevronRight />
                      </Paragraph03>
                      <Paragraph03>
                        {shippingMethodToPrettyName(method)}
                      </Paragraph03>
                    </HStack>
                  )
                )}
              </VStack>
            </VStack>
          </HStack>
        </VStack>
      </Box>
      <Box>
        <OnboardingNavigation
          showBack={showBack}
          readyToContinue={isStepReady()}
          onBack={() => onBack()}
          onNext={() => handleComplete()}
        />
      </Box>
    </Flex>
  );
}

const CountryButton = ({
  countryIso,
  isSelected,
  onCountrySelected = () => {},
}) => {
  const countryName = i18nCountries.getName(countryIso, "es", {
    select: "official",
  });

  return (
    <Button
      borderRadius="brand"
      backgroundColor={useColorModeValue(
        "light.background.lite",
        "dark.background.lite"
      )}
      cursor="pointer"
      colorScheme="gray"
      color={useColorModeValue("light.brand.storng", "dark.brand.strong")}
      h="auto"
      onClick={() => onCountrySelected(countryIso)}
      boxShadow={isSelected ? "0 0 0 3px rgba(52, 93, 238, 1)" : undefined}
    >
      <VStack w="100%" h="100%" px="2" py="4" justify="center">
        <Image
          width="100%"
          maxW="3rem"
          borderRadius="full"
          src={`./flags/flag_${countryIso}.svg`}
          alt={countryName}
        />
        <Paragraph02>{countryName}</Paragraph02>
      </VStack>
    </Button>
  );
};

const availableShippingMethodsForCountryIso = (countryIso) => {
  switch (countryIso) {
    default:
      return ["custom", "store_pickup"];
  }
};

const availablePaymentMethodsForCountryIso = (countryIso) => {
  switch (countryIso) {
    case "ARG":
      return ["custom", "mercadopago"];
    default:
      return ["custom"];
  }
};

const shippingMethodToPrettyName = (shippingMethod) => {
  switch (shippingMethod) {
    case "custom":
      return "Acuerdo con la tienda";
    case "store_pickup":
      return "Retiro por el local";
    default:
      return shippingMethod;
  }
};

const paymentMethodToPrettyName = (paymentMethod) => {
  switch (paymentMethod) {
    case "custom":
      return "Acuerdo con la tienda";
    case "mercadopago":
      return "MercadoPago";
    default:
      return paymentMethod;
  }
};
